import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { useCookies, CookiePreferences } from "@/hooks/useCookies";

const CookieSettings = () => {
  const { cookieConsent, saveCookiePreferences } = useCookies();
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    cookieConsent?.preferences || {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    }
  );

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // No se puede desactivar
    setPreferences(prev => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setShowSettings(false);
  };

  if (!cookieConsent) return null; // Solo mostrar si ya hay consentimiento

  return (
    <>
      <Button
        onClick={() => setShowSettings(true)}
        variant="ghost"
        size="sm"
        className="text-gray-300 hover:text-taxby-purple transition-colors"
      >
        <Settings className="h-4 w-4 mr-2" />
        Configurar cookies
      </Button>

      {/* Panel de configuración */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-taxby-violet">
                  Configuración de Cookies
                </h2>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-6">
                {/* Cookies Necesarias */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Necesarias</h3>
                      <p className="text-sm text-gray-600">Siempre activas</p>
                    </div>
                    <div className="flex items-center">
                      ✓
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.
                  </p>
                </div>

                {/* Cookies Analíticas */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Analíticas</h3>
                      <p className="text-sm text-gray-600">Nos ayudan a mejorar el sitio</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nos permiten analizar el uso del sitio web para mejorar su funcionamiento.
                  </p>
                </div>

                {/* Cookies Funcionales */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Funcionales</h3>
                      <p className="text-sm text-gray-600">Mejoran la funcionalidad</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Permiten que el sitio web recuerde tus preferencias.
                  </p>
                </div>

                {/* Cookies de Marketing */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies de Marketing</h3>
                      <p className="text-sm text-gray-600">Publicidad personalizada</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Se utilizan para mostrar publicidad relevante.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-taxby-purple hover:bg-taxby-purple/90"
                  >
                    Guardar preferencias
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieSettings; 